/**
 * Form field primitives: `Input`, `Select`, `Textarea` and `Checkbox`, each
 * wired to a visible `<label>` and an optional error message, with touch
 * targets of at least `--touch-target-min` (44px) for one-handed field use.
 */
import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

interface FieldWrapperProps {
  id: string;
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
  children: ReactNode;
}

/** Shared label + error markup wrapped around a single form control. */
function FieldWrapper({ id, label, error, hint, required, children }: FieldWrapperProps) {
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="ui-field">
      <label className="ui-field__label" htmlFor={id}>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint ? (
        <p className="ui-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="ui-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

/**
 * Render a labelled text input.
 *
 * @param props - Standard input attributes plus `label`, `error` and `hint`.
 * @returns The field element.
 */
export function Input({ label, error, hint, id, className, required, ...rest }: InputProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const classes = ['ui-input', error ? 'ui-input--error' : '', className].filter(Boolean).join(' ');
  return (
    <FieldWrapper id={fieldId} label={label} error={error} hint={hint} required={required}>
      <input
        id={fieldId}
        className={classes}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        required={required}
        {...rest}
      />
    </FieldWrapper>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  placeholder?: string;
  error?: string | undefined;
  hint?: string | undefined;
}

/**
 * Render a labelled `<select>` with an optional non-selectable placeholder.
 *
 * @param props - Standard select attributes plus `label`, `options`,
 *   `placeholder`, `error` and `hint`.
 * @returns The field element.
 */
export function Select({
  label,
  options,
  placeholder,
  error,
  hint,
  id,
  className,
  required,
  ...rest
}: SelectProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const classes = ['ui-select', error ? 'ui-input--error' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <FieldWrapper id={fieldId} label={label} error={error} hint={hint} required={required}>
      <select
        id={fieldId}
        className={classes}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        required={required}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
}

/**
 * Render a labelled `<textarea>`.
 *
 * @param props - Standard textarea attributes plus `label`, `error` and `hint`.
 * @returns The field element.
 */
export function Textarea({ label, error, hint, id, className, required, ...rest }: TextareaProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const classes = ['ui-textarea', error ? 'ui-input--error' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <FieldWrapper id={fieldId} label={label} error={error} hint={hint} required={required}>
      <textarea
        id={fieldId}
        className={classes}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
        required={required}
        rows={4}
        {...rest}
      />
    </FieldWrapper>
  );
}

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
}

/**
 * Render a labelled checkbox with a ≥44px touch target, label included.
 *
 * @param props - Standard input attributes (type is forced to `"checkbox"`)
 *   plus `label` and `error`.
 * @returns The field element.
 */
export function Checkbox({ label, error, id, className, ...rest }: CheckboxProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const classes = ['ui-checkbox', className].filter(Boolean).join(' ');
  return (
    <div className="ui-field ui-field--checkbox">
      <label className={classes} htmlFor={fieldId}>
        <input
          id={fieldId}
          type="checkbox"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          {...rest}
        />
        <span>{label}</span>
      </label>
      {error ? (
        <p className="ui-field__error" id={`${fieldId}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
